import React, { useState, useRef } from 'react';

import { openInputPicker } from '../../../utils/formUtils/showPickerUtils';

interface DateFieldProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  allowFutureDates?: boolean;
  showTodayLink?: boolean;
}

const sanitizeDateValue = (rawDate: string | undefined | null): string => {
  if (!rawDate) {
    return '';
  }

  const match = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return '';
  }

  const [, year, month, day] = match;
  const yearNum = parseInt(year, 10);
  if (yearNum < 1900) {
    return '';
  }

  return `${year}-${month}-${day}`;
};

const DateField: React.FC<DateFieldProps> = ({
  id,
  value,
  onChange,
  disabled,
  allowFutureDates = true,
  showTodayLink = false,
}) => {
  const safeDate = sanitizeDateValue(value);
  const [showFutureError, setShowFutureError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  const maxDate = allowFutureDates ? undefined : today;

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value;

    // If future dates are not allowed, validate the date on blur
    if (!allowFutureDates && selectedDate && selectedDate > today) {
      setShowFutureError(true);
      e.target.value = '';
      onChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
    } else {
      setShowFutureError(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowFutureError(false);
    onChange(e);
  };

  const handleTodayClick = () => {
    const syntheticEvent = {
      target: {
        id,
        value: today,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    setShowFutureError(false);
    onChange(syntheticEvent);
  };

  return (
    <div className="w-full">
      <input
        type="date"
        id={id}
        ref={inputRef}
        value={safeDate}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={() => openInputPicker(inputRef, disabled)}
        max={maxDate}
        className={`h-10 w-full rounded border px-4 text-sm outline-none transition-all cursor-pointer
          ${
            showFutureError
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-300 dark:border-white/20'
          }
          text-ink dark:bg-white/5 dark:text-white/80
          focus:border-primary dark:focus:border-primary
          disabled:cursor-not-allowed`}
        disabled={disabled}
      />
      {showFutureError && (
        <p className="mt-1 text-xs text-red-500">
          Future dates are not allowed
        </p>
      )}
      {showTodayLink && !disabled && (
        <p className="mt-1 text-xs">
          <span
            onClick={handleTodayClick}
            className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
          >
            Today
          </span>
        </p>
      )}
    </div>
  );
};

export default DateField;
